import { jsx, jsxs, Fragment as Fragment$1 } from '@emotion/react/jsx-runtime';
import { createContext, useState, useRef, useEffect, useCallback, useMemo, useContext, memo, Fragment } from 'react';
import SendbirdChat, { ConnectionHandler } from '@sendbird/chat';
import { GroupChannelModule, MessageFilter, MessageCollectionInitPolicy, GroupChannelHandler, MyMemberStateFilter, GroupChannelListOrder, GroupChannelFilter } from '@sendbird/chat/groupChannel';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

var MessengerGroupTypes = {
    /** 동행찾기 (다수인원) */
    COMPANION: 'COMPANION',
    /** 상품문의 (파트너와 1:1 채널) */
    PRODUCT: 'PRODUCT',
    /** 소셜 (여행자끼리 1:1 채널) */
    SOCIAL: 'SOCIAL',
    /** 광고 (파트너와 1:1 채널) */
    AD: 'AD',
};

var Status = {
    WAIT_CONFIRM: 'waitConfirm',
    CONFIRM: 'confirm',
    REQUEST_CANCEL: 'requestCancel',
    CANCEL: 'cancel',
    WAIT_DEPOSIT: 'waitDeposit',
    WAIT_PAYMENT: 'waitPayment',
    FINISH: 'finish',
};
var StatusMessage = {
    waitConfirm: '예약접수',
    confirm: '예약확정',
    requestCancel: '취소접수',
    cancel: '예약취소',
    waitDeposit: '결제대기',
    waitPayment: '결제대기',
    finish: '여행완료',
};
var MessageEventTypes = {
    MEMBER_LEFT: 'MEMBER_LEFT',
    MEMBER_JOINED: 'MEMBER_JOINED',
    PERSONAL_SERVICE_CHANNEL_CREATED: 'PERSONAL_SERVICE_CHANNEL_CREATED',
    RESERVATION_STATUS_CHANGE: 'RESERVATION_STATUS_CHANGE',
    AD_ONE_IMAGE_CARD_V1: 'AD_ONE_IMAGE_CARD_V1',
    /** 금칙어 발송 탐지 시 메세지 */
    PROFANITY_DETECT: 'PROFANITY_DETECT',
};
var MemberEventStatus = {
    MEMBER_LEFT: 'memberLeft',
    MEMBER_JOINED: 'memberJoined',
};

var MessengerUserTypes = {
    MEMBER: 'MEMBER',
    PROFILE: 'PROFILE',
    SENDBIRD: 'SENDBIRD',
};
var MessengerButtonTypes = {
    NONE: 'NONE',
    ESTIMATE: 'ESTIMATE',
    PRODUCT_DETAIL: 'PRODUCT_DETAIL',
    RESERVATIONS: 'RESERVATIONS',
    SHARE: 'SHARE',
};
// 방장의 권한입니다.
var HostAuthorities = {
    DROP: 'DROP',
};
var userGender = {
    male: '남성',
    female: '여성',
};

var _a;
var MessageErrorType = {
    OVERSIZE: 'oversize',
    SWEAR_WORD: 'swearword',
};
var ErrorMessages = (_a = {},
    _a[MessageErrorType.OVERSIZE] = '파일은 최대 10MB까지 보낼 수 있습니다.\n파일을 다시 확인해주세요.',
    _a[MessageErrorType.SWEAR_WORD] = '대화 내용이 삭제되었습니다.\n욕설 및 개인정보 공유를 삼가주세요.',
    _a);
var SendBirdErrorCode = {
    DISCONNECTED: 800200, // 접속이 끊겼을 때
    TIMEOUT: 800180, // 타임아웃
    FILE_SIZE_EXCEED: 800260, // 용량제한에 걸렸을 때
    FILTERED_PROFANITY: 900060, // 욕하다 걸렸을 때
};
var CHATBOT_CUSTOM_TYPE = 'mrt_chatbot_message';
var CHATBOT_FALLBACK_NICKNAME = '마이리얼트립 AI 상담사';
var CHATBOT_PROFILE_ICON_NAME = 'img_chatbot_profile_26x26';

var MessageType = {
    FILE: 'file',
    USER: 'user',
    ADMIN: 'admin',
};
var SendingStatus = {
    NONE: 'none',
    PENDING: 'pending',
    FAILED: 'failed',
    CANCELED: 'canceled',
    SUCCEEDED: 'succeeded',
};
var SendingStatusText = {
    NONE: '',
    PENDING: '전송중',
    FAILED: '전송실패',
    CANCELED: '전송취소',
    SUCCEEDED: '전송완료',
};
var SendbirdMemberStateFilter = {
    ALL: 'all',
    JOINED_ONLY: 'joined_only',
    INVITED_ONLY: 'invited_only',
    INVITED_BY_FRIEND: 'invited_by_friend',
    INVITED_BY_NON_FRIEND: 'invited_by_non_friend',
};
var SendbirdChannelListOrder = {
    CHRONOLOGICAL: 'chronological',
    LATEST_LAST_MESSAGE: 'latest_last_message',
    CHANNEL_NAME_ALPHABETICAL: 'channel_name_alphabetical',
    METADATA_VALUE_ALPHABETICAL: 'metadata_value_alphabetical',
};

var ReportReason;
(function (ReportReason) {
    ReportReason["OUTER_TRANSACTION"] = "OUTER_TRANSACTION";
    ReportReason["BAD_MANNERS"] = "BAD_MANNERS";
    ReportReason["SWEARWORD"] = "SWEARWORD";
    ReportReason["SEXUAL_HARASSMENT"] = "SEXUAL_HARASSMENT";
    ReportReason["FRAUD"] = "FRAUD";
    ReportReason["ETC"] = "ETC";
})(ReportReason || (ReportReason = {}));
var ReportReasonMap;
(function (ReportReasonMap) {
    ReportReasonMap["OUTER_TRANSACTION"] = "\uC678\uBD80 \uAC70\uB798\uB97C \uC694\uCCAD\uD588\uC5B4\uC694.";
    ReportReasonMap["BAD_MANNERS"] = "\uBE44\uB9E4\uB108 \uC0AC\uC6A9\uC790\uC5D0\uC694.";
    ReportReasonMap["SWEARWORD"] = "\uC695\uC124\uC744 \uD574\uC694.";
    ReportReasonMap["SEXUAL_HARASSMENT"] = "\uC131\uD76C\uB871\uC744 \uD574\uC694.";
    ReportReasonMap["FRAUD"] = "\uC0AC\uAE30\uB97C \uB2F9\uD588\uC5B4\uC694.";
    ReportReasonMap["ETC"] = "\uB2E4\uB978 \uBB38\uC81C\uAC00 \uC788\uC5B4\uC694.";
})(ReportReasonMap || (ReportReasonMap = {}));
var ReportReasonList = [
    ReportReason.OUTER_TRANSACTION,
    ReportReason.BAD_MANNERS,
    ReportReason.SWEARWORD,
    ReportReason.SEXUAL_HARASSMENT,
    ReportReason.FRAUD,
    ReportReason.ETC,
];

var REPORT_DONE_TEXT = '상대방과의 대화가 차단 및 신고되었습니다.';
var BLOCK_DONE_TEXT = '상대방과의 대화가 차단되었습니다.';
var REPORT_CHOICE_TEXT = '신고 사유를 선택해주세요.';
var REPORT_DETAIL_PLACEHOLDER_TEXT = '구체적인 사유를 적어주세요.';
var REPORT_REMIND_TEXT = '상대방을 신고하면 모든 대화를 볼 수 없으며,\n추후 해당 사용자와의 대화방이 생성되지 않습니다. 신고하시겠어요?';
var REPORT_REMIND_TEXT_COMPANION = '신고 후 참여중인 채팅방에서 나가지며,\n신고된 여행자와 새로운 채팅을 생성할 수 없습니다.';
var MOVE_TO_LIST = '대화 목록으로 이동합니다.';
var BLOCK_REMIND_TEXT = '상대방을 차단하면 모든 대화를 볼 수 없으며,\n추후 대화방이 생성되지 않습니다.  차단하시겠어요?';
var BLOCK_REMIND_TEXT_COMPANION = '차단 후 참여중인 채팅방에서 나가지며,\n차단된 여행자와 새로운 채팅을 생성할 수 없습니다.';
var UPLOAD_FILE_SIZE_LIMIT = 10 * 1024 * 1024; //10MB - 게이트웨이에 걸려있는 제한.
var UPLOAD_FILE_OVER_SIZE_MESSAGE = '10MB 이하의 파일을 업로드 해주세요.';
var UPLOAD_FILE_LENGTH_LIMIT = 10;
var UPLOAD_FILE_LENGTH_LIMIT_MESSAGE = '최대 10개까지 업로드 가능합니다.';

var INPUT_MESSAGE_PLACEHOLDER = '메시지를 입력해주세요.';
var INPUT_MESSAGE_PLACEHOLDER_DISABLED = '메시지를 입력할 수 없습니다.';
var INPUT_MESSAGE_PLACEHOLDER_ALONE_DISABLED = '다른 사람이 참여하면 대화할 수 있어요.';
var CONFIRM_RESEND_MESSAGE = '다시 전송하시겠습니까?\n취소 시 삭제됩니다.';
var MEMBER_LEFT_MESSAGE = '상대방에게 대화가 전송되지 않습니다.';

// moment 의존 제거 — Date API로 대체. 결과 포맷은 동일.
var pad2 = function (n) { return n.toString().padStart(2, '0'); };
var getDateText = function (date, isDisplayTime, sendingStatus) {
    if (!isDisplayTime || sendingStatus !== 'succeeded') {
        return null;
    }
    return getDateFormat(date);
};
var getDateFormat = function (date) {
    var d = new Date(date);
    var hours24 = d.getHours();
    var period = hours24 < 12 ? '오전' : '오후';
    var hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return "".concat(period, " ").concat(pad2(hour12), ":").concat(pad2(d.getMinutes()));
};
var getLastMessageDateFormat = function (input) {
    var target = new Date(input);
    if (Number.isNaN(target.getTime()))
        return '';
    var startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    var isToday = target.getTime() >= startOfToday.getTime();
    if (isToday) {
        return "".concat(pad2(target.getHours()), ":").concat(pad2(target.getMinutes()));
    }
    var yy = target.getFullYear() % 100;
    // moment `'YY.M.DD'`와 동일: month는 1자리 가능, day는 항상 2자리.
    return "".concat(pad2(yy), ".").concat(target.getMonth() + 1, ".").concat(pad2(target.getDate()));
};

// 같은 로컬 캘린더 일자인지 비교 — moment 의존 제거.
// Date의 getFullYear/getMonth/getDate는 로컬 타임존 기준이라 moment `.isSame('day')`와 동일 결과.
var isDifferentDay = function (prevDate, nextDate) {
    var prev = new Date(prevDate);
    var next = new Date(nextDate);
    if (Number.isNaN(prev.getTime()) || Number.isNaN(next.getTime())) {
        return true;
    }
    return (prev.getFullYear() !== next.getFullYear() ||
        prev.getMonth() !== next.getMonth() ||
        prev.getDate() !== next.getDate());
};

var hasSender = function (m) {
    return 'sender' in m && m.sender != null;
};
var isDisplayTime = function (message, nextMessage) {
    if (!nextMessage ||
        message.messageType === MessageType.ADMIN ||
        nextMessage.messageType === MessageType.ADMIN) {
        return true;
    }
    if (!hasSender(message) || !hasSender(nextMessage)) {
        return true;
    }
    if (message.sender.userId !== nextMessage.sender.userId) {
        return true;
    }
    // 분(minute) 단위로 다른지 비교 — moment 의존을 제거하고 createdAt(ms)을 60_000으로 나눈 정수 비교로 대체.
    var MINUTE_MS = 60000;
    return (Math.floor(message.createdAt / MINUTE_MS) !== Math.floor(nextMessage.createdAt / MINUTE_MS));
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var isAloneInChannel = function (channel, id) { var _a; return channel.joinedMemberCount === 1 && ((_a = channel.members) === null || _a === void 0 ? void 0 : _a[0].userId) === id; };
var sbInstance = null;
var handlerSeq = 0;
/**
 * core의 SendBird 인스턴스에 접근 (앱 래퍼에서 채널 목록 조회 등에 사용).
 * currentUser가 있을 때만 반환 — init 전(currentUser=null)·disconnect 후(currentUser=null)에는
 * 외부 hook이 stale 인스턴스로 collection을 만들어 이전 user 데이터가 노출되는 회귀를 차단.
 *
 * connectionState === 'OPEN'을 쓰지 않는 이유: setBackgroundState는 WebSocket만 끊고
 * currentUser·캐시는 유지하는데, OPEN 기준이면 백그라운드/재연결 중 null을 반환한다.
 * 그러면 소비처(useGroupChannelCollection)의 sdk가 null이 되어 collection이 dispose되고,
 * 이 함수는 reactive하지 않아 OPEN 복귀 시 재평가가 보장되지 않으므로 채널 목록이
 * 영구히 빈 상태로 남는다. currentUser 기준이면 인스턴스가 안정 유지되어 collection이
 * 살아 있고, 재연결 시 changelog 동기화로 자동 갱신된다.
 */
var getSbInstance = function () { return (sbInstance && sbInstance.currentUser ? sbInstance : null); };
var noop = function () { };
var SendbirdCoreContext = createContext({
    sendbirdUserId: '',
    currentUser: null,
    setCurrentChannel: function () { return null; },
    isAlone: false,
    setIsAlone: function () { return null; },
    messages: null,
    setMessages: function () { return null; },
    mediaViewer: { isOpen: false },
    setMediaViewer: function () { return null; },
    canReadMore: false,
    initialLoadError: false,
    retryInitialLoad: noop,
    newPrevMessages: null,
    receivedNewMessage: false,
    handleChangeFalseReceivedNewMessage: noop,
    getPreviousMessages: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
    getMorePreviousMessages: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
    sendMessage: noop,
    sendFileMessage: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
    resendMessage: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
    deleteMessage: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
    markAsRead: noop,
    getConnectionState: function () { return ''; },
    enterBackground: noop,
    enterForeground: noop,
    signOut: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
});
/**
 * SendbirdProvider — SDK 초기화 + MessageCollection 기반 메시지 관리
 * 모든 상태와 기능을 Context에 제공하여 단일 인스턴스로 관리
 */
var SendbirdProvider = function (_a) {
    var appId = _a.appId, userId = _a.userId, accessToken = _a.accessToken, channelId = _a.channelId, children = _a.children, onMessageFailed = _a.onMessageFailed, onConfirmResend = _a.onConfirmResend;
    var _b = useState(null), connectedUser = _b[0], setConnectedUser = _b[1];
    var _c = useState(undefined), currentChannel = _c[0], setCurrentChannel = _c[1];
    var _d = useState(false), isAlone = _d[0], setIsAlone = _d[1];
    var _e = useState(null), messages = _e[0], setMessages = _e[1];
    var _f = useState({ isOpen: false }), mediaViewer = _f[0], setMediaViewer = _f[1];
    var _g = useState(false), canReadMore = _g[0], setCanReadMore = _g[1];
    var _h = useState(false), receivedNewMessage = _h[0], setReceivedNewMessage = _h[1];
    // 초기 메시지 로드(API replace)가 실패했는지 — 소비처가 "이전 없음" 대신 재시도 UI를 띄우는 데 사용.
    var _j = useState(false), initialLoadError = _j[0], setInitialLoadError = _j[1];
    var collectionRef = useRef(null);
    var collectionReadyRef = useRef(false);
    var currentChannelRef = useRef(null);
    var onMessageFailedRef = useRef(onMessageFailed);
    var onConfirmResendRef = useRef(onConfirmResend);
    useEffect(function () {
        onMessageFailedRef.current = onMessageFailed;
        onConfirmResendRef.current = onConfirmResend;
    }, [onMessageFailed, onConfirmResend]);
    // userId === undefined인 첫 렌더에서 `String(undefined)` → `"undefined"`가 Context로 누출되는 것을 차단.
    // (소비자가 이 값으로 비교/표시하는 코드를 안전하게 만든다)
    var sendbirdUserId = userId == null ? '' : String(userId);
    // ─── SDK 초기화 ───
    var initSbInstance = useCallback(function () {
        if (sbInstance && sbInstance.appId !== appId) {
            sbInstance.disconnect();
            sbInstance = null;
        }
        if (!sbInstance) {
            // localCacheEnabled:false — 영속 IndexedDB 캐시의 stale/eviction으로 hasPrevious가
            // 잘못 false로 굳어 "이전 메시지가 없습니다"가 sticky하게 노출되던 버그 클래스를 제거한다.
            // (V4 캐시 도입 이전 V3와 동일한 server-truth 동작) MessageCollection/핸들러/
            // hasPrevious/loadPrevious/changelog sync는 cache off에서도 동작한다.
            // 트레이드오프: enableAutoResend(LocalCacheConfig 전용) 자동 재전송·오프라인 읽기 상실,
            // cold load마다 API 조회(= V3 수준). 재전송은 resendMessage로 수동 처리한다.
            sbInstance = SendbirdChat.init({
                appId: appId,
                modules: [new GroupChannelModule()],
                localCacheEnabled: false,
            });
        }
        return sbInstance;
    }, [appId]);
    var getChannel = useCallback(function (channelUrl) { return __awaiter(void 0, void 0, void 0, function () {
        var sb, error_1, sbError;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    sb = initSbInstance();
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, sb.groupChannel.getChannel(channelUrl)];
                case 2: return [2 /*return*/, _a.sent()];
                case 3:
                    error_1 = _a.sent();
                    sbError = error_1;
                    console.error("[Sendbird::ErrCode-".concat(sbError.code, "]"), sbError.message);
                    throw error_1;
                case 4: return [2 /*return*/];
            }
        });
    }); }, [initSbInstance]);
    var init = useCallback(function (id, token, isCancelled) { return __awaiter(void 0, void 0, void 0, function () {
        var sb, error_2, sbError, connectedInstance, _a, error_3, sbError;
        var _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    if (!id || id === 'NaN' || id === 'undefined') {
                        return [2 /*return*/, false];
                    }
                    sb = initSbInstance();
                    // 이미 같은 유저로 연결된 상태면 재연결하지 않음 (Strict Mode 대응)
                    if (sb.connectionState === 'OPEN' && ((_b = sb.currentUser) === null || _b === void 0 ? void 0 : _b.userId) === id) {
                        if (isCancelled === null || isCancelled === void 0 ? void 0 : isCancelled())
                            return [2 /*return*/, false];
                        setConnectedUser(sb.currentUser);
                        return [2 /*return*/, sb.currentUser];
                    }
                    if (!(sb.connectionState === 'OPEN' && ((_c = sb.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== id)) return [3 /*break*/, 4];
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, sb.disconnect()];
                case 2:
                    _d.sent();
                    return [3 /*break*/, 4];
                case 3:
                    error_2 = _d.sent();
                    sbError = error_2;
                    console.error("[Sendbird:init:disconnect:ErrCode-".concat(sbError.code, "]"), sbError.message);
                    return [3 /*break*/, 4];
                case 4:
                    _d.trys.push([4, 9, , 10]);
                    if (!token) return [3 /*break*/, 6];
                    return [4 /*yield*/, sb.connect(id, token)];
                case 5:
                    _a = _d.sent();
                    return [3 /*break*/, 8];
                case 6: return [4 /*yield*/, sb.connect(id)];
                case 7:
                    _a = _d.sent();
                    _d.label = 8;
                case 8:
                    connectedInstance = _a;
                    // connect는 비동기이므로 resolve 시점에 이미 effect cleanup(언마운트/userId 변경/signOut)이
                    // 일어났을 수 있다. 가드 없이 setter를 부르면 로그아웃된 user가 복원되는 race가 발생.
                    if (isCancelled === null || isCancelled === void 0 ? void 0 : isCancelled())
                        return [2 /*return*/, false];
                    setConnectedUser(connectedInstance);
                    return [2 /*return*/, connectedInstance];
                case 9:
                    error_3 = _d.sent();
                    sbError = error_3;
                    console.error("[Sendbird:init:ErrCode-".concat(sbError.code, "]"), sbError.message);
                    return [2 /*return*/, false];
                case 10: return [2 /*return*/];
            }
        });
    }); }, [initSbInstance]);
    useEffect(function () {
        var cancelled = false;
        if (userId) {
            init(sendbirdUserId, accessToken, function () { return cancelled; });
        }
        else {
            // userId prop이 falsy로 전환되는 경우(=로그아웃). connectionState와 무관하게 항상 정리한다.
            // 백그라운드(setBackgroundState)에서는 connectionState !== 'OPEN'이지만 currentUser·캐시가
            // 남아 있어, OPEN 기준으로 가드하면 백그라운드 로그아웃 시 정리가 스킵된다. 그러면
            // getSbInstance()(currentUser 기준)가 이전 user 인스턴스를 계속 반환해 외부 hook이
            // 이전 user의 채널/메시지에 접근하는 PII 누수가 발생한다.
            if (sbInstance === null || sbInstance === void 0 ? void 0 : sbInstance.currentUser) {
                sbInstance.disconnect().catch(function (error) {
                    var sbError = error;
                    console.error("[Sendbird:autoDisconnect:ErrCode-".concat(sbError.code, "]"), sbError.message);
                });
            }
            setConnectedUser(null);
        }
        return function () {
            cancelled = true;
        };
    }, [init, sendbirdUserId, userId, accessToken]);
    // ─── channelId 변경 대응 (단일 Provider 구조) ───
    useEffect(function () {
        if (!connectedUser || !channelId) {
            // 채널 미선택/연결 해제 시 MessageCollection 정리
            if (collectionRef.current) {
                collectionRef.current.dispose();
                collectionRef.current = null;
                currentChannelRef.current = null;
                setMessages(null);
                setCurrentChannel(undefined);
                setIsAlone(false);
            }
            return;
        }
        var cancelled = false;
        var loadChannel = function () { return __awaiter(void 0, void 0, void 0, function () {
            var channel;
            var _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, getChannel(channelId)];
                    case 1:
                        channel = _c.sent();
                        if (cancelled)
                            return [2 /*return*/];
                        if (channel) {
                            currentChannelRef.current = channel;
                            setCurrentChannel(channel);
                            setIsAlone(isAloneInChannel(channel, sendbirdUserId));
                            initMessageCollection(channel);
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        _c.sent();
                        // getChannel은 내부에서 이미 로깅됨. 실패 시 stale UI 방지를 위해 상태 초기화.
                        if (cancelled)
                            return [2 /*return*/];
                        (_b = collectionRef.current) === null || _b === void 0 ? void 0 : _b.dispose();
                        collectionRef.current = null;
                        currentChannelRef.current = null;
                        setMessages(null);
                        setCurrentChannel(undefined);
                        setIsAlone(false);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); };
        loadChannel();
        return function () {
            cancelled = true;
        };
        // sendbirdUserId 변경 시(=계정 전환) 같은 channelId라도 effect를 재실행해야
        // 이전 user의 채널이 새 user 화면에 그대로 노출되는 누수를 막는다.
        // initMessageCollection, getChannel 등은 의도적으로 deps에서 제외 (무한 루프 방지).
    }, [channelId, connectedUser, sendbirdUserId]);
    // ─── MessageCollection ───
    var initMessageCollection = useCallback(function (channel) {
        var _a, _b;
        // 같은 채널의 Collection이 이미 있으면 재생성하지 않음
        if (collectionRef.current && ((_a = collectionRef.current.channel) === null || _a === void 0 ? void 0 : _a.url) === channel.url) {
            return;
        }
        // 기존 Collection 정리 (다른 채널일 때만)
        (_b = collectionRef.current) === null || _b === void 0 ? void 0 : _b.dispose();
        collectionReadyRef.current = false;
        // 새 (재)초기화 시작 — 이전 채널/이전 시도의 상태를 초기화한다.
        // 특히 messages를 비우지 않으면 새 collection의 onApiResult가 오기 전까지 이전 채널
        // 메시지가 화면에 남아 노출된다(PII). cache off에선 초기 로드가 cache 즉시 렌더 없이
        // API에만 의존해 더 길어지므로 명시적으로 리셋한다. messages=null은 '로딩 중'을 의미하며
        // 소비처는 이때 더보기/"이전 없음"을 렌더하지 않는다(깜빡임 방지).
        setInitialLoadError(false);
        setMessages(null);
        setCanReadMore(false);
        var filter = new MessageFilter();
        var collection = channel.createMessageCollection({
            filter: filter,
            prevResultLimit: 100,
            startingPoint: Date.now(),
        });
        // 채널 전환 시 stale 콜백이 새 collection 상태를 덮어쓰지 않도록
        // ref 동등성으로 식별. 이벤트 핸들러 등록 전에 ref를 먼저 갱신해야
        // 동기적으로 fire되는 콜백도 새 collection을 인식한다.
        collectionRef.current = collection;
        var isCurrent = function () { return collectionRef.current === collection; };
        // succeededMessages를 단일 source of truth로 사용 (정렬 일관성)
        collection.setMessageCollectionHandler({
            onMessagesAdded: function () {
                if (!isCurrent())
                    return;
                setReceivedNewMessage(true);
                setMessages(__spreadArray([], collection.succeededMessages, true));
            },
            onMessagesUpdated: function () {
                if (!isCurrent())
                    return;
                setMessages(__spreadArray([], collection.succeededMessages, true));
            },
            onMessagesDeleted: function () {
                if (!isCurrent())
                    return;
                setMessages(__spreadArray([], collection.succeededMessages, true));
            },
            onChannelUpdated: function () { },
            onChannelDeleted: function () { },
            onHugeGapDetected: function () {
                if (!isCurrent())
                    return;
                // 진단 계측: huge gap(>300건 누락) 복구 트리거 빈도/채널 파악
                console.info("[Sendbird:hugeGap] url=".concat(channel.url));
                // 등록 시점의 `channel` 스냅샷은 멤버 변경 등으로 stale일 수 있으므로
                // 현재 채널 ref를 사용하여 재초기화. 채널이 바뀐 상태면 재초기화하지 않는다.
                var latestChannel = currentChannelRef.current;
                var stillCurrent = (latestChannel === null || latestChannel === void 0 ? void 0 : latestChannel.url) === channel.url;
                collection.dispose();
                collectionRef.current = null;
                if (stillCurrent && latestChannel) {
                    initMessageCollection(latestChannel);
                }
            },
        });
        collection
            .initialize(MessageCollectionInitPolicy.CACHE_AND_REPLACE_BY_API)
            .onCacheResult(function (err) {
            if (!isCurrent() || err)
                return;
            // cache off에선 캐시가 비어 succeededMessages가 []로 온다. 이를 setMessages([])로
            // 반영하면 '로딩 완료(빈 채널)'로 오인돼 "이전 없음"이 조기에 깜빡인다.
            // 캐시에 실제 메시지가 있을 때만 선반영하고, 비었으면 onApiResult를 기다린다.
            if (collection.succeededMessages.length > 0) {
                setMessages(__spreadArray([], collection.succeededMessages, true));
            }
        })
            .onApiResult(function (err) {
            if (!isCurrent())
                return;
            var sbErr = err;
            if (sbErr) {
                // 진단 계측(실패 시에만): cache off로 eviction 기인 오표시는 제거되므로, 이후 "이전 없음"이
                // 다시 보인다면 원인은 API 교체 실패다. 에러코드·hasPrevious·msgs·플랫폼(UA)을 함께 남겨
                // 빈도/영향 채널/플랫폼 분포를 파악한다. (성공 hot path 로그는 측정 가치가 없어 제거)
                var ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
                console.error("[Sendbird:apiResult:err] code=".concat(sbErr.code, " url=").concat(channel.url, " ") +
                    "hasPrevious=".concat(collection.hasPrevious, " msgs=").concat(collection.succeededMessages.length, " ua=").concat(ua));
                // 결함 ①: API 교체가 실패하면 hasPrevious는 신뢰할 수 없다(cache off에선 빈 상태).
                // 이를 "이전 메시지 없음"으로 단정하면 안 되므로 setCanReadMore를 호출하지 않고
                // 직전 값을 보존한다. 초기 로드 실패는 소비처가 재시도 UI를 띄울 수 있도록 플래그로 노출.
                setInitialLoadError(true);
                return;
            }
            setMessages(__spreadArray([], collection.succeededMessages, true));
            collectionReadyRef.current = true;
            setInitialLoadError(false);
            setCanReadMore(collection.hasPrevious);
        });
    }, []);
    // ─── 메시지 조회 ───
    var getPreviousMessages = useCallback(function (channel) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (!channel) {
                console.warn('no target channel');
                return [2 /*return*/];
            }
            initMessageCollection(channel);
            return [2 /*return*/];
        });
    }); }, [initMessageCollection]);
    var getMorePreviousMessages = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var collection, olderMsgs_1, error_4, sbError;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    collection = collectionRef.current;
                    if (!collection || !collectionReadyRef.current || !collection.hasPrevious) {
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, collection.loadPrevious()];
                case 2:
                    olderMsgs_1 = _a.sent();
                    // await 도중에 채널이 전환되어 collection이 교체되면 이전 채널 메시지가
                    // 새 채널 UI에 prepend되어 메시지가 섞이는 회귀가 발생한다.
                    if (collectionRef.current !== collection)
                        return [2 /*return*/];
                    setMessages(function (prev) { return __spreadArray(__spreadArray([], olderMsgs_1, true), (prev !== null && prev !== void 0 ? prev : []), true); });
                    setCanReadMore(collection.hasPrevious);
                    return [3 /*break*/, 4];
                case 3:
                    error_4 = _a.sent();
                    sbError = error_4;
                    console.error("[Sendbird::ErrCode-".concat(sbError.code, "]"), sbError.message);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, []);
    // 초기 로드 실패(initialLoadError) 시 현재 채널의 collection을 강제 재초기화한다.
    // 같은 채널이면 initMessageCollection이 early-return하므로 ref를 먼저 비운다.
    var retryInitialLoad = useCallback(function () {
        var _a;
        var channel = currentChannelRef.current;
        if (!channel)
            return;
        (_a = collectionRef.current) === null || _a === void 0 ? void 0 : _a.dispose();
        collectionRef.current = null;
        collectionReadyRef.current = false;
        initMessageCollection(channel);
    }, [initMessageCollection]);
    var handleChangeFalseReceivedNewMessage = useCallback(function () {
        setReceivedNewMessage(false);
    }, []);
    // ─── 메시지 전송 ───
    var sendMessage = useCallback(function (message) {
        if (!currentChannel || message === '') {
            console.error('[Sendbird]', !currentChannel ? 'No currentChannel' : 'Empty message');
            return;
        }
        currentChannel
            .sendUserMessage({ message: message })
            .onFailed(function (err, failedMessage) {
            var _a, _b;
            var sbErr = err;
            console.error("[Sendbird:SendMessage:ErrCode-".concat(sbErr.code, "]"), sbErr.message);
            if (sbErr.code === SendBirdErrorCode.DISCONNECTED) {
                sbInstance === null || sbInstance === void 0 ? void 0 : sbInstance.reconnect();
            }
            (_a = onMessageFailedRef.current) === null || _a === void 0 ? void 0 : _a.call(onMessageFailedRef, (_b = failedMessage) !== null && _b !== void 0 ? _b : null, sbErr);
        });
        // Collection의 onMessagesAdded가 pending/succeeded 상태를 자동 반영
    }, [currentChannel]);
    var sendFileMessage = useCallback(function (files) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (!currentChannel || files.length === 0) {
                return [2 /*return*/];
            }
            Array.from(files).forEach(function (file) {
                var params = {
                    file: file,
                    fileName: file.name,
                    fileSize: file.size,
                    mimeType: file.type,
                };
                if (file.type.includes('video') ||
                    (file.type.includes('image') && !file.type.includes('heic') && !file.type.includes('svg'))) {
                    params.thumbnailSizes = [{ maxWidth: 320, maxHeight: 320 }];
                }
                currentChannel.sendFileMessage(params).onFailed(function (err, failedMessage) {
                    var _a, _b;
                    var sbErr = err;
                    console.error("[Sendbird:SendFile:ErrCode-".concat(sbErr.code, "]"), sbErr.message);
                    if (sbErr.code === SendBirdErrorCode.DISCONNECTED) {
                        sbInstance === null || sbInstance === void 0 ? void 0 : sbInstance.reconnect();
                    }
                    (_a = onMessageFailedRef.current) === null || _a === void 0 ? void 0 : _a.call(onMessageFailedRef, (_b = failedMessage) !== null && _b !== void 0 ? _b : null, sbErr);
                });
            });
            return [2 /*return*/];
        });
    }); }, [currentChannel]);
    var deleteMessage = useCallback(function (message) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!currentChannel || !message) {
                        console.error('[Sendbird]', !currentChannel ? 'No currentChannel' : 'Empty message');
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, currentChannel.deleteMessage(message)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [currentChannel]);
    var resendMessage = useCallback(function (message) { return __awaiter(void 0, void 0, void 0, function () {
        var confirmer, confirmed, _a, collection;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!currentChannel || !message) {
                        console.error('[Sendbird]', !currentChannel ? 'No currentChannel' : 'Empty message');
                        return [2 /*return*/];
                    }
                    confirmer = onConfirmResendRef.current;
                    if (!confirmer) return [3 /*break*/, 2];
                    return [4 /*yield*/, confirmer()];
                case 1:
                    _a = _b.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = true;
                    _b.label = 3;
                case 3:
                    confirmed = _a;
                    if (!confirmed) return [3 /*break*/, 4];
                    if (message.messageType === 'file') {
                        currentChannel.resendFileMessage(message);
                    }
                    else {
                        currentChannel.resendUserMessage(message);
                    }
                    return [3 /*break*/, 6];
                case 4:
                    collection = collectionRef.current;
                    if (!collection) return [3 /*break*/, 6];
                    return [4 /*yield*/, collection.removeFailedMessage(message.reqId)];
                case 5:
                    _b.sent();
                    _b.label = 6;
                case 6: return [2 /*return*/];
            }
        });
    }); }, [currentChannel]);
    var markAsRead = useCallback(function () {
        if (!currentChannel) {
            return;
        }
        // WebSocket이 닫힌 상태(setBackgroundState/pagehide/네트워크 단절 직후 등)에서 markAsRead를 호출하면
        // SDK가 connectionClosed Promise를 던져 unhandled rejection으로 Sentry에 누적된다.
        if ((sbInstance === null || sbInstance === void 0 ? void 0 : sbInstance.connectionState) !== 'OPEN') {
            return;
        }
        currentChannel.markAsRead().catch(function (error) {
            console.error("[Sendbird:markAsRead:ErrCode-".concat(error.code, "]"), error.message);
        });
    }, [currentChannel]);
    // ─── Event Handlers ───
    useEffect(function () {
        if (!sbInstance)
            return;
        var sb = sbInstance;
        // Date.now() ms 정밀도로는 Strict Mode 빠른 mount/unmount/remount 시 ID 충돌 가능 →
        // SDK가 동일 ID 핸들러를 silently 덮어쓰는 회귀를 막기 위해 모듈 단조증가 카운터 사용.
        handlerSeq += 1;
        var HANDLER_ID = "messenger_core_".concat(sendbirdUserId, "_").concat(handlerSeq);
        sb.groupChannel.addGroupChannelHandler(HANDLER_ID, new GroupChannelHandler({
            onUserJoined: function (channel) {
                setIsAlone(isAloneInChannel(channel, sendbirdUserId));
            },
            onUserLeft: function (channel) {
                setIsAlone(isAloneInChannel(channel, sendbirdUserId));
            },
            onUnreadMemberStatusUpdated: function () {
                setMessages(function (prev) { return (prev ? __spreadArray([], prev, true) : prev); });
            },
        }));
        sb.addConnectionHandler(HANDLER_ID, new ConnectionHandler({
            onReconnectStarted: function () { },
            onReconnectSucceeded: function () { },
            onReconnectFailed: function () { },
        }));
        return function () {
            sb.groupChannel.removeGroupChannelHandler(HANDLER_ID);
            sb.removeConnectionHandler(HANDLER_ID);
        };
    }, [connectedUser, sendbirdUserId]);
    // Collection cleanup on unmount
    useEffect(function () {
        return function () {
            var _a;
            (_a = collectionRef.current) === null || _a === void 0 ? void 0 : _a.dispose();
        };
    }, []);
    // pagehide 발생 시(WebView 종료/탭 닫기/페이지 unload) setBackgroundState 로 전환하여
    // 사용자가 다른 사용자에게 online 으로 잘못 노출되지 않게 한다. WebSocket 은 즉시 끊기지만
    // 세션·localCache 는 유지되므로 재진입 시 setForegroundState 로 빠르게 복원 가능.
    useEffect(function () {
        if (typeof window === 'undefined')
            return;
        var handlePageHide = function () {
            var sb = sbInstance;
            if (sb && sb.connectionState === 'OPEN') {
                try {
                    sb.setBackgroundState();
                }
                catch (error) {
                    var sbError = error;
                    console.error("[Sendbird:pagehide:ErrCode-".concat(sbError.code, "]"), sbError.message);
                }
            }
        };
        window.addEventListener('pagehide', handlePageHide);
        return function () {
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, []);
    // ─── 백그라운드/포어그라운드 전환 + 로그아웃 ───
    var enterBackground = useCallback(function (callBackFn) {
        if (!connectedUser || !sbInstance)
            return;
        // setBackgroundState: WebSocket만 끊고 currentUser와 캐시(Collection 포함)를 유지
        // @see https://sendbird.com/docs/chat/sdk/v4/javascript
        sbInstance.setBackgroundState();
        if (callBackFn)
            callBackFn();
    }, [connectedUser]);
    var enterForeground = useCallback(function (callBackFn) {
        if (!connectedUser || !sbInstance)
            return;
        // setForegroundState: WebSocket 재연결, Collection 이벤트 자동 재개
        sbInstance.setForegroundState();
        if (callBackFn)
            callBackFn();
    }, [connectedUser]);
    var signOut = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_5, sbError;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!sbInstance)
                        return [2 /*return*/];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    (_a = collectionRef.current) === null || _a === void 0 ? void 0 : _a.dispose();
                    collectionRef.current = null;
                    currentChannelRef.current = null;
                    return [4 /*yield*/, sbInstance.disconnect()];
                case 2:
                    _b.sent();
                    return [3 /*break*/, 5];
                case 3:
                    error_5 = _b.sent();
                    sbError = error_5;
                    console.error("[Sendbird:signOut:ErrCode-".concat(sbError.code, "]"), sbError.message);
                    return [3 /*break*/, 5];
                case 4:
                    setConnectedUser(null);
                    setCurrentChannel(undefined);
                    setMessages(null);
                    setIsAlone(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, []);
    var getConnectionState = useCallback(function () {
        var _a;
        return (_a = sbInstance === null || sbInstance === void 0 ? void 0 : sbInstance.connectionState) !== null && _a !== void 0 ? _a : '';
    }, []);
    // ─── Context ───
    // 매 렌더마다 새 객체가 되면 useSendbirdCore를 쓰는 모든 소비자가 강제 리렌더된다.
    // 메시지 1건이 오는 매 setReceivedNewMessage(true) 호출에도 화면 전체가 리렌더되는
    // 비용을 줄이기 위해 useMemo로 referential stability 확보.
    var contextValues = useMemo(function () { return ({
        currentUser: connectedUser,
        sendbirdUserId: sendbirdUserId,
        currentChannel: currentChannel,
        setCurrentChannel: setCurrentChannel,
        isAlone: isAlone,
        setIsAlone: setIsAlone,
        messages: messages,
        setMessages: setMessages,
        mediaViewer: mediaViewer,
        setMediaViewer: setMediaViewer,
        canReadMore: canReadMore,
        initialLoadError: initialLoadError,
        retryInitialLoad: retryInitialLoad,
        newPrevMessages: null,
        receivedNewMessage: receivedNewMessage,
        handleChangeFalseReceivedNewMessage: handleChangeFalseReceivedNewMessage,
        getPreviousMessages: getPreviousMessages,
        getMorePreviousMessages: getMorePreviousMessages,
        sendMessage: sendMessage,
        sendFileMessage: sendFileMessage,
        resendMessage: resendMessage,
        deleteMessage: deleteMessage,
        markAsRead: markAsRead,
        getConnectionState: getConnectionState,
        enterBackground: enterBackground,
        enterForeground: enterForeground,
        signOut: signOut,
    }); }, [
        connectedUser,
        sendbirdUserId,
        currentChannel,
        isAlone,
        messages,
        mediaViewer,
        canReadMore,
        initialLoadError,
        retryInitialLoad,
        receivedNewMessage,
        handleChangeFalseReceivedNewMessage,
        getPreviousMessages,
        getMorePreviousMessages,
        sendMessage,
        sendFileMessage,
        resendMessage,
        deleteMessage,
        markAsRead,
        getConnectionState,
        enterBackground,
        enterForeground,
        signOut,
    ]);
    return (jsx(SendbirdCoreContext.Provider, { value: contextValues, children: children }));
};
/**
 * Hook: core Sendbird 기능
 * Provider의 Context에서 모든 기능을 읽어옴 — 로컬 상태 없음
 */
var useSendbirdCore = function () {
    return useContext(SendbirdCoreContext);
};

var getInitialVisibility = function () { return (typeof document !== 'undefined' ? !document.hidden : true); };
function usePageVisibility() {
    var _a = useState(getInitialVisibility), isVisible = _a[0], setVisible = _a[1];
    var isVisibleRef = useRef(isVisible);
    var onVisibilityChange = useCallback(function () {
        var nowVisible = !document.hidden;
        if (nowVisible !== isVisibleRef.current) {
            isVisibleRef.current = nowVisible;
            setVisible(nowVisible);
        }
    }, []);
    useEffect(function () {
        if (typeof document === 'undefined')
            return;
        document.addEventListener('visibilitychange', onVisibilityChange, false);
        return function () {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [onVisibilityChange]);
    return { isVisible: isVisible };
}

var DEFAULT_FILTER = {
    includeEmpty: true,
    myMemberStateFilter: MyMemberStateFilter.JOINED,
};
// 키 순서에 의존하지 않는 안정적인 직렬화 (얕은 깊이 — 필터 객체는 1-depth 가정).
var stableStringify = function (obj) {
    var record = obj;
    return JSON.stringify(Object.keys(record)
        .sort()
        .reduce(function (acc, k) {
        acc[k] = record[k];
        return acc;
    }, {}));
};
function useGroupChannelCollection(_a) {
    var _this = this;
    var sdk = _a.sdk, filterParams = _a.filter, _b = _a.order, order = _b === void 0 ? GroupChannelListOrder.LATEST_LAST_MESSAGE : _b, _c = _a.limit, limit = _c === void 0 ? 20 : _c;
    var _d = useState([]), channels = _d[0], setChannels = _d[1];
    var _e = useState(false), isLoading = _e[0], setIsLoading = _e[1];
    var _f = useState(false), isInitialized = _f[0], setIsInitialized = _f[1];
    var _g = useState(false), hasMore = _g[0], setHasMore = _g[1];
    var collectionRef = useRef(null);
    var isLoadingRef = useRef(false);
    // filter는 객체이므로 직렬화하여 안정적인 deps로 사용.
    // 키 순서가 같은 두 객체(`{a:1,b:2}` vs `{b:2,a:1}`)가 다른 문자열로 직렬화되면 매 렌더마다
    // collection이 dispose/재생성되며 채널 목록 API가 반복 호출된다. 키 정렬로 정규화.
    var filterKey = stableStringify(filterParams !== null && filterParams !== void 0 ? filterParams : DEFAULT_FILTER);
    useEffect(function () {
        var _a, _b;
        // sdk 해제 시 이전 collection·상태 모두 정리
        if (!sdk) {
            (_a = collectionRef.current) === null || _a === void 0 ? void 0 : _a.dispose();
            collectionRef.current = null;
            setChannels([]);
            setHasMore(false);
            setIsLoading(false);
            setIsInitialized(true);
            return;
        }
        setIsLoading(true);
        setIsInitialized(false);
        setChannels([]);
        setHasMore(false);
        // 이전 collection 정리
        (_b = collectionRef.current) === null || _b === void 0 ? void 0 : _b.dispose();
        var disposed = false;
        var filter = new GroupChannelFilter(filterParams !== null && filterParams !== void 0 ? filterParams : DEFAULT_FILTER);
        var collectionParams = { filter: filter, order: order, limit: limit };
        var collection = sdk.groupChannel.createGroupChannelCollection(collectionParams);
        collectionRef.current = collection;
        // 이벤트 핸들러: collection.channels 기반 (SDK가 정렬 관리).
        // ref 동등성으로 stale collection 콜백 무시.
        var isCurrent = function () { return collectionRef.current === collection; };
        collection.setGroupChannelCollectionHandler({
            onChannelsAdded: function () {
                if (!disposed && isCurrent())
                    setChannels(__spreadArray([], collection.channels, true));
            },
            onChannelsUpdated: function () {
                if (!disposed && isCurrent())
                    setChannels(__spreadArray([], collection.channels, true));
            },
            onChannelsDeleted: function () {
                if (!disposed && isCurrent())
                    setChannels(__spreadArray([], collection.channels, true));
            },
        });
        // 초기 로드
        var loadInitial = function () { return __awaiter(_this, void 0, void 0, function () {
            var error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setIsLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, 5, 6]);
                        if (!collection.hasMore) return [3 /*break*/, 3];
                        // loadMore 반환값(`loaded`)은 "이번 호출에서 로드된 채널" 의미라 SDK 내부 cache+API
                        // merge 결과에서 같은 channelUrl의 별도 인스턴스가 함께 포함될 수 있음(SDK 미dedupe).
                        // 반면 collection.channels는 SDK가 channelUrl을 unique key로 관리하는 deduped 누적.
                        // 다른 4곳(handler/loadMore)이 모두 collection.channels를 쓰므로 소스를 통일한다.
                        return [4 /*yield*/, collection.loadMore()];
                    case 2:
                        // loadMore 반환값(`loaded`)은 "이번 호출에서 로드된 채널" 의미라 SDK 내부 cache+API
                        // merge 결과에서 같은 channelUrl의 별도 인스턴스가 함께 포함될 수 있음(SDK 미dedupe).
                        // 반면 collection.channels는 SDK가 channelUrl을 unique key로 관리하는 deduped 누적.
                        // 다른 4곳(handler/loadMore)이 모두 collection.channels를 쓰므로 소스를 통일한다.
                        _a.sent();
                        if (!disposed && isCurrent()) {
                            setChannels(__spreadArray([], collection.channels, true));
                            setHasMore(collection.hasMore);
                        }
                        _a.label = 3;
                    case 3: return [3 /*break*/, 6];
                    case 4:
                        error_1 = _a.sent();
                        console.error('[Sendbird:GroupChannelCollection] Initial load error:', error_1);
                        return [3 /*break*/, 6];
                    case 5:
                        if (!disposed && isCurrent()) {
                            setIsLoading(false);
                            setIsInitialized(true);
                        }
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        }); };
        loadInitial();
        return function () {
            disposed = true;
            collection.dispose();
            if (collectionRef.current === collection) {
                collectionRef.current = null;
            }
        };
    }, [sdk, filterKey, order, limit]);
    // 추가 로드 (페이지네이션)
    var loadMore = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var collection, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    collection = collectionRef.current;
                    if (!collection || !collection.hasMore || isLoadingRef.current) {
                        return [2 /*return*/];
                    }
                    // 주의: 페이지네이션 loadMore는 list-level `isLoading`을 토글하지 않는다.
                    // isLoading=true가 소비처(useMyChannels)에서 `channels=null`로 전파되면 기존 목록이
                    // 통째로 "불러오는 중" 로더로 교체되어 문서 높이가 붕괴 → 스크롤이 위로 튀는 회귀가 발생한다.
                    // 동시 호출 방지는 isLoadingRef로 충분하고, 하단 "더 불러오는 중" 표시는 hasMore로 처리한다.
                    isLoadingRef.current = true;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, collection.loadMore()];
                case 2:
                    _a.sent();
                    // collection이 교체되거나 dispose된 경우 stale 응답으로 새 상태 덮어쓰지 않도록 가드
                    if (collectionRef.current !== collection) {
                        return [2 /*return*/];
                    }
                    setChannels(__spreadArray([], collection.channels, true));
                    setHasMore(collection.hasMore);
                    return [3 /*break*/, 5];
                case 3:
                    error_2 = _a.sent();
                    console.error('[Sendbird:GroupChannelCollection] loadMore error:', error_2);
                    return [3 /*break*/, 5];
                case 4:
                    isLoadingRef.current = false;
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, []);
    return {
        channels: channels,
        hasMore: hasMore,
        loadMore: loadMore,
        // sdk 존재 + 초기 로드 미완료 → 로딩 중
        isLoading: isLoading || (!!sdk && !isInitialized),
    };
}

function useChannelEnrichment(_a) {
    var _this = this;
    var channelUrls = _a.channelUrls, fetchEnrichment = _a.fetchEnrichment, _b = _a.enabled, enabled = _b === void 0 ? true : _b;
    var _c = useState(new Map()), enrichmentMap = _c[0], setEnrichmentMap = _c[1];
    var _d = useState(false), isLoading = _d[0], setIsLoading = _d[1];
    var fetchEnrichmentRef = useRef(fetchEnrichment);
    var enrichmentMapRef = useRef(enrichmentMap);
    // ref mutation은 commit phase의 effect에서 수행해야 React 18 동시성 모드에서
    // 같은 컴포넌트가 두 번 render돼도 안전하다.
    useEffect(function () {
        fetchEnrichmentRef.current = fetchEnrichment;
        enrichmentMapRef.current = enrichmentMap;
    });
    // 정렬·중복 제거된 JSON.stringify로 effect 트리거 안정화.
    // 단순 join(',')은 channelUrl에 ','가 포함되거나 순서만 다르면 키가 달라져 불필요한 재호출이 발생.
    var urlsKey = JSON.stringify(Array.from(new Set(channelUrls)).sort());
    useEffect(function () {
        if (!enabled || channelUrls.length === 0) {
            // fetch 진행 중에 channelUrls가 비워지면 이전 effect cleanup으로 isLoading=false가
            // 호출되지 않을 수 있어, early return 분기에서 명시적으로 로딩 플래그를 내려준다.
            setIsLoading(false);
            return;
        }
        // ref를 통해 항상 최신 enrichmentMap 참조 (stale closure 방지)
        var newUrls = channelUrls.filter(function (url) { return !enrichmentMapRef.current.has(url); });
        if (newUrls.length === 0) {
            setIsLoading(false);
            return;
        }
        var cancelled = false;
        var fetchNew = function () { return __awaiter(_this, void 0, void 0, function () {
            var result_1, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setIsLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, fetchEnrichmentRef.current(newUrls)];
                    case 2:
                        result_1 = _a.sent();
                        if (cancelled)
                            return [2 /*return*/];
                        setEnrichmentMap(function (prev) {
                            var next = new Map(prev);
                            Object.entries(result_1).forEach(function (_a) {
                                var url = _a[0], data = _a[1];
                                next.set(url, data);
                            });
                            return next;
                        });
                        return [3 /*break*/, 5];
                    case 3:
                        error_1 = _a.sent();
                        console.error('[Sendbird:Enrichment] Batch fetch error:', error_1);
                        return [3 /*break*/, 5];
                    case 4:
                        if (!cancelled) {
                            setIsLoading(false);
                        }
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }); };
        fetchNew();
        return function () {
            cancelled = true;
        };
    }, [urlsKey, enabled]);
    // 단건 갱신
    var refreshChannel = useCallback(function (channelUrl) { return __awaiter(_this, void 0, void 0, function () {
        var result, data_1, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, fetchEnrichmentRef.current([channelUrl])];
                case 1:
                    result = _a.sent();
                    data_1 = result[channelUrl];
                    if (data_1) {
                        setEnrichmentMap(function (prev) {
                            var next = new Map(prev);
                            next.set(channelUrl, data_1);
                            return next;
                        });
                    }
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    console.error('[Sendbird:Enrichment] Single refresh error:', error_2);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, []);
    return { enrichmentMap: enrichmentMap, isLoading: isLoading, refreshChannel: refreshChannel };
}

var KO_WEEKDAYS = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
var formatKoreanDate = function (timestamp) {
    var d = new Date(timestamp);
    return "".concat(d.getMonth() + 1, "\uC6D4 ").concat(d.getDate(), "\uC77C ").concat(KO_WEEKDAYS[d.getDay()]);
};
var DateDivider = function (_a) {
    var createdAt = _a.createdAt;
    return jsx(DateDividerContainer, { children: formatKoreanDate(createdAt) });
};
var DateDividerContainer = styled.div(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n  padding: 28px 0 22px;\n  color: var(--gray-600);\n  text-align: center;\n  font-size: 13px;\n  clear: both;\n"], ["\n  padding: 28px 0 22px;\n  color: var(--gray-600);\n  text-align: center;\n  font-size: 13px;\n  clear: both;\n"])));
var DateDivider$1 = memo(DateDivider);
var templateObject_1$4;

var ErrorMessage = function (_a) {
    var errorType = _a.errorType;
    var errorMessage = ErrorMessages[errorType];
    if (!errorMessage) {
        return null;
    }
    return (jsx(FileSizeErrorMessageContainer, { children: jsx(Contents$1, { children: jsx("span", { children: jsx(BlockMessage, { children: errorMessage }) }) }) }));
};
var FileSizeErrorMessageContainer = styled.div(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row-reverse;\n  align-items: flex-start;\n  padding: 0 10px;\n  margin-top: 6px;\n"], ["\n  display: flex;\n  flex-direction: row-reverse;\n  align-items: flex-start;\n  padding: 0 10px;\n  margin-top: 6px;\n"])));
var BlockMessage = styled.span(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n  display: block;\n  padding: 9px 12px 7px;\n  border-radius: 12px;\n  background-color: var(--gray-200);\n  color: var(--gray-500);\n  word-break: break-all;\n  white-space: pre-line;\n  line-height: 20px;\n  font-size: 15px;\n"], ["\n  display: block;\n  padding: 9px 12px 7px;\n  border-radius: 12px;\n  background-color: var(--gray-200);\n  color: var(--gray-500);\n  word-break: break-all;\n  white-space: pre-line;\n  line-height: 20px;\n  font-size: 15px;\n"])));
var Contents$1 = styled.div(templateObject_3$2 || (templateObject_3$2 = __makeTemplateObject(["\n  display: inline-block;\n  max-width: 70%;\n"], ["\n  display: inline-block;\n  max-width: 70%;\n"])));
var ErrorMessage$1 = memo(ErrorMessage);
var templateObject_1$3, templateObject_2$2, templateObject_3$2;

var NetworkErrorMessage = function (_a) {
    var sendingStatus = _a.sendingStatus;
    var isFailed = sendingStatus === SendingStatus.FAILED;
    var isPending = sendingStatus === SendingStatus.PENDING;
    // FAILED/PENDING이 아닌 상태(SUCCEEDED, CANCELED, NONE, null 등)에서는 렌더링하지 않음
    if (!isFailed && !isPending) {
        return null;
    }
    return (jsx(Fragment, { children: isFailed ? (jsxs("div", { children: ["\uB124\uD2B8\uC6CC\uD06C\uAC00 \uBD88\uC548\uC815\uD569\uB2C8\uB2E4.", jsx("br", {}), "\uB124\uD2B8\uC6CC\uD06C\uB97C \uD655\uC778\uD574\uC8FC\uC138\uC694."] })) : (jsxs(Fragment$1, { children: [jsx(LoadingIcon, {}), SendingStatusText.PENDING] })) }));
};
var LoadingIcon = styled.span(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n  display: inline-block;\n  width: 10px;\n  height: 10px;\n  margin-right: 2px;\n  // \uC624\uD504\uB77C\uC778\uC2DC\uC5D0 \uBCF4\uC5EC\uC918\uC57C\uD558\uB294 \uB85C\uB529 \uC560\uB2C8\uBA54\uC774\uC158 - \uB124\uD2B8\uC6CC\uD06C \uD1B5\uC2E0\uC774 \uBD88\uAC00\uD558\uAE30 \uB54C\uBB38\uC5D0 css\uB85C \uCC98\uB9AC\n  background: no-repeat 50% 50%\n    url(data:image/svg+xml;base64,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);\n  background-size: 10px 10px;\n"], ["\n  display: inline-block;\n  width: 10px;\n  height: 10px;\n  margin-right: 2px;\n  // \uC624\uD504\uB77C\uC778\uC2DC\uC5D0 \uBCF4\uC5EC\uC918\uC57C\uD558\uB294 \uB85C\uB529 \uC560\uB2C8\uBA54\uC774\uC158 - \uB124\uD2B8\uC6CC\uD06C \uD1B5\uC2E0\uC774 \uBD88\uAC00\uD558\uAE30 \uB54C\uBB38\uC5D0 css\uB85C \uCC98\uB9AC\n  background: no-repeat 50% 50%\n    url(data:image/svg+xml;base64,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);\n  background-size: 10px 10px;\n"])));
var NetworkErrorMessage$1 = memo(NetworkErrorMessage);
var templateObject_1$2;

var isSafeUrl = function (url) { return /^https?:\/\//i.test(url); };
var SitePreview = function (_a) {
    var ogMeta = _a.ogMeta, onLinkClick = _a.onLinkClick;
    var url = ogMeta.url, title = ogMeta.title, description = ogMeta.description, defaultImage = ogMeta.defaultImage;
    var safeHref = isSafeUrl(url) ? url : undefined;
    // OG 이미지 URL을 그대로 렌더하면 발신자 서버가 추적 픽셀로 악용 가능
    // (수신자 IP/UA/Referer/조회 시각 누출). 동일한 isSafeUrl 검증 + no-referrer로 차단.
    var safeImageSrc = (defaultImage === null || defaultImage === void 0 ? void 0 : defaultImage.url) && isSafeUrl(defaultImage.url) ? defaultImage.url : undefined;
    var handleClick = function (event) {
        if (!safeHref) {
            event.preventDefault();
            return;
        }
        if (onLinkClick) {
            event.preventDefault();
            onLinkClick(safeHref);
        }
        // 핸들러 미제공 시 anchor 기본 동작(target=_blank + rel)을 따라간다
    };
    return (jsxs(Container, { className: "webSitePreview", href: safeHref, target: "_blank", rel: "noopener noreferrer", onClick: handleClick, children: [safeImageSrc && (jsx("img", { alt: "", src: safeImageSrc, width: "100%", loading: "lazy", referrerPolicy: "no-referrer" })), jsxs(Info, { children: [jsx(Title, { children: title }), jsx(Description, { children: description })] })] }));
};
var Container = styled.a(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  display: block;\n  background: var(--gray-50);\n"], ["\n  display: block;\n  background: var(--gray-50);\n"])));
var Info = styled.div(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n  padding: 12px;\n"], ["\n  padding: 12px;\n"])));
var Title = styled.div(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n  overflow: hidden;\n  color: var(--gray-900);\n  white-space: nowrap;\n  text-overflow: ellipsis;\n  font-size: 14px;\n"], ["\n  overflow: hidden;\n  color: var(--gray-900);\n  white-space: nowrap;\n  text-overflow: ellipsis;\n  font-size: 14px;\n"])));
var Description = styled.div(templateObject_4$1 || (templateObject_4$1 = __makeTemplateObject(["\n  overflow: hidden;\n  color: var(--gray-400);\n  white-space: nowrap;\n  text-overflow: ellipsis;\n"], ["\n  overflow: hidden;\n  color: var(--gray-400);\n  white-space: nowrap;\n  text-overflow: ellipsis;\n"])));
var SitePreview$1 = memo(SitePreview);
var templateObject_1$1, templateObject_2$1, templateObject_3$1, templateObject_4$1;

var MessageContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  align-items: flex-start;\n  padding: 0 10px;\n  margin-top: 6px;\n  outline: none;\n\n  &.myMessage {\n    flex-direction: row-reverse;\n  }\n\n  &:not(.myMessage) + .myMessage,\n  .myMessage + &:not(.myMessage) {\n    margin-top: 20px;\n  }\n"], ["\n  display: flex;\n  flex-direction: row;\n  align-items: flex-start;\n  padding: 0 10px;\n  margin-top: 6px;\n  outline: none;\n\n  &.myMessage {\n    flex-direction: row-reverse;\n  }\n\n  &:not(.myMessage) + .myMessage,\n  .myMessage + &:not(.myMessage) {\n    margin-top: 20px;\n  }\n"])));
var Contents = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: inline-block;\n  max-width: 75%;\n"], ["\n  display: inline-block;\n  max-width: 75%;\n"])));
var Profile = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: inline-block;\n  overflow: hidden;\n  width: 26px;\n  min-width: 26px;\n  height: 26px;\n  margin: 2px 8px 0 0;\n  border-radius: 50%;\n  box-shadow: 0 0 1px rgba(0, 0, 0, 0.4);\n  background: var(--messenger-default-profile-url, '') no-repeat;\n\n  & > img {\n    width: 26px;\n    height: 26px;\n    object-fit: cover;\n  }\n  .myMessage & {\n    display: none;\n  }\n"], ["\n  display: inline-block;\n  overflow: hidden;\n  width: 26px;\n  min-width: 26px;\n  height: 26px;\n  margin: 2px 8px 0 0;\n  border-radius: 50%;\n  box-shadow: 0 0 1px rgba(0, 0, 0, 0.4);\n  background: var(--messenger-default-profile-url, '') no-repeat;\n\n  & > img {\n    width: 26px;\n    height: 26px;\n    object-fit: cover;\n  }\n  .myMessage & {\n    display: none;\n  }\n"])));
var NicknameText = styled.span(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: block;\n  margin-bottom: 8px;\n  color: var(--gray-600);\n  font-size: 13px;\n\n  .myMessage & {\n    display: none;\n  }\n"], ["\n  display: block;\n  margin-bottom: 8px;\n  color: var(--gray-600);\n  font-size: 13px;\n\n  .myMessage & {\n    display: none;\n  }\n"])));
var MessageWrapper = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  align-items: flex-start;\n\n  .myMessage & {\n    flex-direction: row-reverse;\n  }\n"], ["\n  display: flex;\n  flex-direction: row;\n  align-items: flex-start;\n\n  .myMessage & {\n    flex-direction: row-reverse;\n  }\n"])));
var Message = styled.div(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  display: block;\n  overflow: hidden;\n  border-radius: 18px;\n  background-color: var(--gray-80);\n\n  .myMessage & {\n    background-color: var(--blue-400);\n\n    a {\n      color: var(--white);\n    }\n  }\n\n  a {\n    color: var(--gray-900);\n  }\n"], ["\n  display: block;\n  overflow: hidden;\n  border-radius: 18px;\n  background-color: var(--gray-80);\n\n  .myMessage & {\n    background-color: var(--blue-400);\n\n    a {\n      color: var(--white);\n    }\n  }\n\n  a {\n    color: var(--gray-900);\n  }\n"])));
var textStyle = css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  display: block;\n  padding: 9px 12px 7px;\n  color: var(--gray-900);\n  word-break: break-all;\n  white-space: pre-wrap;\n  line-height: 20px;\n  font-size: 15px;\n\n  &.blockMessage {\n    background-color: var(--gray-200);\n    color: var(--gray-500);\n  }\n\n  .myMessage & {\n    color: var(--white);\n  }\n"], ["\n  display: block;\n  padding: 9px 12px 7px;\n  color: var(--gray-900);\n  word-break: break-all;\n  white-space: pre-wrap;\n  line-height: 20px;\n  font-size: 15px;\n\n  &.blockMessage {\n    background-color: var(--gray-200);\n    color: var(--gray-500);\n  }\n\n  .myMessage & {\n    color: var(--white);\n  }\n"])));
var Text = styled.span(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), textStyle);
var TextLink = styled.a(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n  ", "\n  text-decoration: underline;\n"], ["\n  ", "\n  text-decoration: underline;\n"])), textStyle);
var MessageStatus = styled.div(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n  align-self: flex-end;\n  padding: 0 5px;\n  text-align: right;\n  line-height: 13px;\n  font-size: 11px;\n  color: var(--gray-400);\n\n  .warning {\n    color: var(--red-600);\n  }\n"], ["\n  align-self: flex-end;\n  padding: 0 5px;\n  text-align: right;\n  line-height: 13px;\n  font-size: 11px;\n  color: var(--gray-400);\n\n  .warning {\n    color: var(--red-600);\n  }\n"])));
var DateText = styled.span(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n  display: block;\n  white-space: nowrap;\n"], ["\n  display: block;\n  white-space: nowrap;\n"])));
var IconDocument = styled.span(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n  display: inline-block;\n  width: 32px;\n  height: 32px;\n  margin-right: 10px;\n  border-radius: 6px;\n  background: var(--messenger-icon-document-url, '') var(--white) no-repeat 50% 50%;\n  vertical-align: middle;\n"], ["\n  display: inline-block;\n  width: 32px;\n  height: 32px;\n  margin-right: 10px;\n  border-radius: 6px;\n  background: var(--messenger-icon-document-url, '') var(--white) no-repeat 50% 50%;\n  vertical-align: middle;\n"])));
var adminTextStyles = css(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n  font-weight: 500;\n  font-size: 14px;\n  line-height: 19px;\n  letter-spacing: -0.01em;\n  color: var(--gray-800);\n\n  word-break: keep-all;\n  white-space: pre-wrap;\n"], ["\n  font-weight: 500;\n  font-size: 14px;\n  line-height: 19px;\n  letter-spacing: -0.01em;\n  color: var(--gray-800);\n\n  word-break: keep-all;\n  white-space: pre-wrap;\n"])));
var AdminMessageContainer = styled.div(templateObject_14 || (templateObject_14 = __makeTemplateObject(["\n  margin-top: 6px;\n  padding: 12px;\n"], ["\n  margin-top: 6px;\n  padding: 12px;\n"])));
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14;

export { AdminMessageContainer, BLOCK_DONE_TEXT, BLOCK_REMIND_TEXT, BLOCK_REMIND_TEXT_COMPANION, CHATBOT_CUSTOM_TYPE, CHATBOT_FALLBACK_NICKNAME, CHATBOT_PROFILE_ICON_NAME, CONFIRM_RESEND_MESSAGE, Contents, DateDivider$1 as DateDivider, DateText, ErrorMessage$1 as ErrorMessage, ErrorMessages, HostAuthorities, INPUT_MESSAGE_PLACEHOLDER, INPUT_MESSAGE_PLACEHOLDER_ALONE_DISABLED, INPUT_MESSAGE_PLACEHOLDER_DISABLED, IconDocument, MEMBER_LEFT_MESSAGE, MOVE_TO_LIST, MemberEventStatus, Message, MessageContainer, MessageErrorType, MessageEventTypes, MessageStatus, MessageType, MessageWrapper, MessengerButtonTypes, MessengerGroupTypes, MessengerUserTypes, NetworkErrorMessage$1 as NetworkErrorMessage, NicknameText, Profile, REPORT_CHOICE_TEXT, REPORT_DETAIL_PLACEHOLDER_TEXT, REPORT_DONE_TEXT, REPORT_REMIND_TEXT, REPORT_REMIND_TEXT_COMPANION, ReportReason, ReportReasonList, ReportReasonMap, SendBirdErrorCode, SendbirdChannelListOrder, SendbirdMemberStateFilter, SendbirdProvider, SendingStatus, SendingStatusText, SitePreview$1 as SitePreview, Status, StatusMessage, Text, TextLink, UPLOAD_FILE_LENGTH_LIMIT, UPLOAD_FILE_LENGTH_LIMIT_MESSAGE, UPLOAD_FILE_OVER_SIZE_MESSAGE, UPLOAD_FILE_SIZE_LIMIT, adminTextStyles, getDateText, getLastMessageDateFormat, getSbInstance, isDifferentDay, isDisplayTime, useChannelEnrichment, useGroupChannelCollection, usePageVisibility, useSendbirdCore, userGender };
//# sourceMappingURL=index.esm.js.map
